import { connect } from 'react-redux';
import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { Group } from '../../../../types';
import {
  SearchPostResultDto,
  SearchSessionResultDto,
  SearchCategoryResultDto,
  SearchResultsInDto,
} from '../../../../types/search.dto';
import Icon from '../../../../components/Icon';
import bookSvg from '../../../../assets/icons/book.svg';
import videoSvg from '../../../../assets/icons/video-recorder.svg';
import calendarSvg from '../../../../assets/icons/calendar.svg';
import categorySvg from '../../../../assets/icons/category.svg';
import {
  Panel,
  Section,
  SectionHeader,
  SectionLabel,
  ResultRow,
  ResultText,
  ResultTitle,
  ResultSubtitle,
  ResultBadge,
  EmptyState,
  ErrorState,
  ShowMoreBtn,
  SkeletonRow,
  SkeletonIcon,
  SkeletonLine,
  SkeletonBadge,
} from './styles';

interface ComponentProps {
  query: string;
  group: Group;
  results: SearchResultsInDto | null;
  states: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
}

/** Wraps query terms in <strong> for purple highlight */
const highlight = (text: string, query: string): JSX.Element => {
  if (!query.trim()) return <>{text}</>;

  const escapedTerms = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  const splitRegex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
  const matchRegex = new RegExp(`^(${escapedTerms.join('|')})$`, 'i');
  const parts = text.split(splitRegex);

  return (
    <>
      {parts.map((part, i) =>
        matchRegex.test(part) ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
      )}
    </>
  );
};

const formatSessionDate = (datetime: string | null): string => {
  if (!datetime) return '';
  const date = new Date(datetime);
  const now = new Date();
  const isPast = date < now;
  const day = date.getDate();
  const month = date.toLocaleString('es', { month: 'short' });
  return isPast ? `Grabación · ${day} ${month}` : `${day} ${month}`;
};

const SkeletonSection = ({ rows = 2 }: { rows?: number }) => (
  <Section>
    <SectionHeader>
      <SkeletonIcon style={{ width: 16, height: 16, borderRadius: '50%' }} />
      <SkeletonLine width="80px" />
    </SectionHeader>
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonRow key={i}>
        <SkeletonIcon />
        <SkeletonLine width={i % 2 === 0 ? '55%' : '70%'} />
        <SkeletonBadge />
      </SkeletonRow>
    ))}
  </Section>
);

const POSTS_INITIAL_LIMIT = 5;

const Component = ({ query, group, results, states }: ComponentProps) => {
  const { isMobile } = useWindowSize();
  const theme = useTheme() as any;
  const [showAllPosts, setShowAllPosts] = useState(false);

  useEffect(() => {
    setShowAllPosts(false);
  }, [query]);

  if (states.loading) {
    return (
      <Panel>
        <SkeletonSection rows={2} />
        <SkeletonSection rows={1} />
        <SkeletonSection rows={1} />
      </Panel>
    );
  }

  if (states.error) {
    return (
      <Panel>
        <ErrorState>Error al buscar, intenta de nuevo</ErrorState>
      </Panel>
    );
  }

  const isEmpty =
    !results ||
    ((results.posts?.length ?? 0) === 0 &&
      (results.sessions?.length ?? 0) === 0 &&
      (results.categories?.length ?? 0) === 0);

  if (states.success && isEmpty) {
    return (
      <Panel>
        <EmptyState>Sin resultados para «{query}»</EmptyState>
      </Panel>
    );
  }

  if (!results) return null;

  const hasPosts = (results?.posts?.length ?? 0) > 0;
  const hasSessions = (results?.sessions?.length ?? 0) > 0;
  const hasCategories = (results?.categories?.length ?? 0) > 0;

  const isReallyEmpty = !hasPosts && !hasSessions && !hasCategories;
  if (isReallyEmpty) {
    return (
      <Panel>
        <EmptyState>Sin resultados para «{query}»</EmptyState>
      </Panel>
    );
  }

  return (
    <Panel>
      {hasPosts && (
        <Section>
          <SectionHeader>
            <Icon icon={bookSvg} size="15px" color={theme.colors.Primary} />
            <SectionLabel>Artículos</SectionLabel>
          </SectionHeader>
          {(showAllPosts ? results.posts : results.posts.slice(0, POSTS_INITIAL_LIMIT)).map((post: SearchPostResultDto) => {
            const isScheduled = !post.published_at;
            const categoryName = post.content_categories?.[0]?.name;
            const badge = isScheduled ? 'Programado' : post.is_free ? 'Liberado' : categoryName || null;

            return (
              <ResultRow
                key={`search-post-${post.id}`}
                href={`/group/${group?.id}/p/${post.id}`}
              >
                <Icon icon={bookSvg} size="18px" color="#636E72" />
                <ResultText>
                  <ResultTitle>{highlight(post.title, query)}</ResultTitle>
                  {post.short_description && (
                    <ResultSubtitle>{post.short_description}</ResultSubtitle>
                  )}
                </ResultText>
                {!isMobile && badge && <ResultBadge>{badge}</ResultBadge>}
              </ResultRow>
            );
          })}
          {!showAllPosts && results.posts.length > POSTS_INITIAL_LIMIT && (
            <ShowMoreBtn type="button" onClick={() => setShowAllPosts(true)}>
              Ver {results.posts.length - POSTS_INITIAL_LIMIT} artículos más
            </ShowMoreBtn>
          )}
        </Section>
      )}

      {hasSessions && (
        <Section>
          <SectionHeader>
            <Icon icon={videoSvg} size="15px" color={theme.colors.Primary} />
            <SectionLabel>Encuentros</SectionLabel>
          </SectionHeader>
          {results.sessions.map((session: SearchSessionResultDto) => {
            const dateLabel = formatSessionDate(session.session_datetime);
            const isPast = session.session_datetime
              ? new Date(session.session_datetime) < new Date()
              : false;
            const badge = isPast ? 'Grabación' : dateLabel || null;

            return (
              <ResultRow
                key={`search-session-${session.id}`}
                href={`/group/${group?.slug}/s/${session.id}`}
              >
                <Icon icon={isPast ? videoSvg : calendarSvg} size="18px" color="#636E72" />
                <ResultText>
                  <ResultTitle>{highlight(session.title, query)}</ResultTitle>
                  {session.speaker_name && (
                    <ResultSubtitle>{session.speaker_name}</ResultSubtitle>
                  )}
                </ResultText>
                {badge && <ResultBadge>{badge}</ResultBadge>}
              </ResultRow>
            );
          })}
        </Section>
      )}

      {hasCategories && (
        <Section>
          <SectionHeader>
            <Icon icon={categorySvg} size="15px" color={theme.colors.Primary} />
            <SectionLabel>Categorías</SectionLabel>
          </SectionHeader>
          {results.categories.map((category: SearchCategoryResultDto) => (
            <ResultRow
              key={`search-category-${category.id}`}
              href={`/group/${group?.slug}/p/c/${category.id}`}
            >
              <Icon icon={categorySvg} size="18px" color="#636E72" />
              <ResultText>
                <ResultTitle>{highlight(category.name, query)}</ResultTitle>
                {category.description && (
                  <ResultSubtitle>{category.description}</ResultSubtitle>
                )}
              </ResultText>
              <ResultBadge>{category.post_count} posts</ResultBadge>
            </ResultRow>
          ))}
        </Section>
      )}
    </Panel>
  );
};

const state = ({ searchStore, groupStore }) => {
  const { data: results, states } = searchStore.search;
  const { data: group } = groupStore.group;

  return {
    results,
    states,
    group,
  };
};

export default connect(state)(Component);
